import React, { Fragment, useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { BrandType, MealCategoryType, MealType } from 'utils/types';
import { apiService } from 'services/base.service';
import CategoryPricingCard from 'components/category-pricing-card';
import PageText from 'components/base/page-text';

const Pricing = () => {
  const { activeCokitchen, category } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [brandsList, setBrandsList] = useState<BrandType[]>([]);
  const [activeBrand, setActiveBrand] = useState<BrandType | undefined>(undefined);
  const [mealCategoriesList, setMealCategoriesList] = useState<MealCategoryType[] | undefined>([]);
  const [mealList, setMealList] = useState<Record<string, MealType[]>>({});
  const [mealLoading, setMealLoading] = useState<Record<string, boolean>>({});
  useEffect(() => {
    fetchData();
  }, [category, activeCokitchen]);
  useEffect(() => {
    if (activeBrand) {
      setMealCategoriesList(activeBrand?.meal_category);
    }
  }, [activeBrand]);
  const fetchData = async () => {
    if (!category) return;
    try {
      setLoading(true);
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const data = res.data?.filter(
        (b: BrandType) => b.item_type === category.toUpperCase() && !b.archived
      );
      setBrandsList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mealCategoriesList) {
      mealCategoriesList?.forEach((mealCategory: MealCategoryType) => {
        fetchMealData(mealCategory.id);
      });
    }
  }, [mealCategoriesList]);
  const fetchMealData = async (id: string) => {
    setMealLoading((prev) => ({
      ...prev,
      [id]: true
    }));
    try {
      const response = await apiService.get(`marketing/meal-categories/meals/${id}`, {
        isInternal: true
      });
      const { meals } = response.data.data;
      setMealList((prev) => ({
        ...prev,
        [id]: meals
      }));
    } finally {
      setMealLoading((prev) => ({
        ...prev,
        [id]: false
      }));
    }
  };

  useEffect(() => {
    if (brandsList.length > 0) {
      setActiveBrand(brandsList[0]);
    }
  }, [brandsList]);

  return (
    <div className='w-full'>
      {loading ? (
        <PageText text='Loading...' />
      ) : (
        <Fragment>
          <div className='w-full overflow-x-scroll flex gap-6 mb-4 pb-4 mt-2'>
            {brandsList.map((brand: BrandType) => (
              <div
                className={`h-[37px] min-w-fit rounded-[20px] py-[6px] px-3 bg-gray-9 flex gap-2 items-center cursor-pointer ${
                  activeBrand?.id === brand.id && '!bg-gray-6'
                }`}
                key={brand.id}
                onClick={() => {
                  setActiveBrand(brand);
                }}>
                <div
                  className={`font-medium text-gray-5 text-base ${
                    activeBrand?.id === brand.id && 'text-white'
                  }`}>
                  {brand.name}
                </div>
                <div
                  className={`w-6 h-6 rounded-[20px] flex justify-center items-center bg-gray-2 text-xs text-gray-5  ${
                    activeBrand?.id === brand.id && 'text-white !bg-[#3D3D3D]'
                  }`}>
                  {brand?.meal_category?.length}
                </div>
              </div>
            ))}
          </div>
          {mealCategoriesList?.length === 0 ? (
            <PageText text='No data found' />
          ) : (
            <div className='w-full overflow-x-scroll flex gap-4 pb-4 mt-4'>
              {mealCategoriesList?.map((mealCategory: MealCategoryType) => {
                return (
                  <CategoryPricingCard
                    key={mealCategory.id}
                    mealList={mealList[mealCategory.id] ?? []}
                    setMealList={(value) => {
                      setMealList((prev) => ({
                        ...prev,
                        [mealCategory.id]: value
                      }));
                    }}
                    mealLoading={mealLoading[mealCategory.id] ?? false}
                  />
                );
              })}
            </div>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default Pricing;
